import { makeObservable } from "mobx";

import type { Uuid } from "@/domains/global/identifiers";
import type { AudioUploadProgressStateReader } from "@/store/audio/uploading/audio-uploading.types";
import type { AudioRecordingTransformProgressTracker } from "@/store/audio/transforming/audio-transforming.types";
import {
  AudioRecordingProcessingStatus,
  AudioRecordingUploadStatus,
} from "@/store/audio-recordings/types";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import {
  AudioRecordingSuggestionState,
  AudioRecordingSuggestionStatus,
  IAudioSuggestionStore,
} from "@/store/audio/suggestion/audio-suggestion.types";

type AppStoreAudioSuggestionStoreArgs = AppSubStoreArgs & {
  uploadingStore: AudioUploadProgressStateReader;
  transformingStore: AudioRecordingTransformProgressTracker;
};

export class AppStoreAudioSuggestionStore extends AppSubStore implements IAudioSuggestionStore {
  private readonly uploadingStore: AudioUploadProgressStateReader;
  private readonly transformingStore: AudioRecordingTransformProgressTracker;

  constructor(injectedDeps: AppStoreAudioSuggestionStoreArgs) {
    super(injectedDeps);

    this.uploadingStore = injectedDeps.uploadingStore;
    this.transformingStore = injectedDeps.transformingStore;

    makeObservable<AppStoreAudioSuggestionStore, "uploadingStore" | "transformingStore">(this, {
      uploadingStore: false,
      transformingStore: false,
      getSuggestionState: false,
      isSuggestionReady: false,
    });
  }

  getSuggestionState({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): AudioRecordingSuggestionState {
    const recording = this.store.audioRecordings.get(audioRecordingId);

    if (!recording) {
      return {
        status: AudioRecordingSuggestionStatus.Idle,
        errorMessage: null,
      };
    }

    const noteId = recording.associatedNoteId as Uuid | undefined;
    const noteObservable = noteId ? this.store.notes.getNoteObservableById({ noteId }) : null;
    const isSuggestionActive = Boolean(noteObservable?.isSuggestionActive);

    const uploadState = this.uploadingStore.getProgress({ audioRecordingId });
    const transformState = this.transformingStore.getTransformState({ audioRecordingId });

    const uploadErrorMessage = uploadState.errorMessage ?? recording.uploadErrorMessage ?? null;
    const transformErrorMessage =
      transformState.errorMessage ?? recording.transformErrorMessage ?? null;

    if (recording.uploadStatus === AudioRecordingUploadStatus.SKIPPED) {
      return {
        status: AudioRecordingSuggestionStatus.Error,
        errorMessage: "Upload skipped",
      };
    }

    if (uploadErrorMessage || recording.uploadStatus === AudioRecordingUploadStatus.ERROR) {
      return {
        status: AudioRecordingSuggestionStatus.Error,
        errorMessage: uploadErrorMessage ?? "Upload failed",
      };
    }

    if (
      transformErrorMessage ||
      recording.processingStatus === AudioRecordingProcessingStatus.ERROR
    ) {
      return {
        status: AudioRecordingSuggestionStatus.Error,
        errorMessage: transformErrorMessage ?? "Processing failed",
      };
    }

    if (isSuggestionActive) {
      return {
        status: AudioRecordingSuggestionStatus.Ready,
        errorMessage: null,
      };
    }

    if (
      uploadState.isUploading ||
      recording.uploadStatus === AudioRecordingUploadStatus.IN_PROGRESS
    ) {
      return {
        status: AudioRecordingSuggestionStatus.WaitingForUpload,
        errorMessage: null,
      };
    }

    if (
      transformState.isTransforming ||
      recording.processingStatus === AudioRecordingProcessingStatus.IN_PROGRESS
    ) {
      return {
        status: AudioRecordingSuggestionStatus.WaitingForProcessing,
        errorMessage: null,
      };
    }

    if (
      recording.uploadStatus === AudioRecordingUploadStatus.COMPLETED &&
      recording.processingStatus === AudioRecordingProcessingStatus.COMPLETED
    ) {
      return {
        status: AudioRecordingSuggestionStatus.AwaitingSuggestion,
        errorMessage: null,
      };
    }

    return {
      status: AudioRecordingSuggestionStatus.Idle,
      errorMessage: null,
    };
  }

  isSuggestionReady({ audioRecordingId }: { audioRecordingId: Uuid }): boolean {
    return (
      this.getSuggestionState({ audioRecordingId }).status === AudioRecordingSuggestionStatus.Ready
    );
  }
}
